import { PERCIX_EXTENSION_ID } from '../externalConfig';

// Shared helper for reading the Percix browser extension id off the
// document. The chrome-extension's percix-portal-bridge content script
// (isolated world) stashes the id on
// `<html data-percix-extension-id="…">` because the page CSP forbids
// inline `<script>` injection. Both `useEmrExtension` and
// `runActionClient` need this id whenever they fall back to the
// chrome.runtime transport.

// The set of extension ids this build will talk to.
//
// The bridge attribute is writable by any content script in the page, so a
// co-installed hostile extension can advertise its own id and receive the
// portal's runAction — payload included. Pinning removes that: the DOM value is
// treated as a PROPOSAL and accepted only if it is on this list.
//
// Why a LIST and not a single id: a Microsoft Edge Add-ons listing is RE-SIGNED
// by Microsoft, so the same extension published to both stores has two
// different ids (Chrome derives an id from the signing key). Edge installing
// *from the Chrome Web Store* — what clinicians do today — keeps the Chrome id
// and needs no second entry; a native Edge listing does.
//
// An empty list = unpinned: fall back to whatever the DOM says, which local dev
// and unpacked installs need (their ids differ per install).
//
// A nonce echoed back in the pong was considered and rejected: the impostor is
// the RECIPIENT of the ping, so it learns the nonce and echoes it correctly. A
// nonce proves liveness, not identity.
const PINNED_IDS: readonly string[] = PERCIX_EXTENSION_ID.split(',')
  .map((id) => id.trim())
  .filter((id) => id.length > 0);

// The id used when nothing is on the DOM to check against — the first entry, so
// a single-store build behaves exactly as before. With several stores the DOM
// value decides which of them is actually installed; this is only the fallback
// for a page where no bridge has stamped anything yet.
const PRIMARY_PINNED_ID = PINNED_IDS[0];

const ATTR = 'percixExtensionId';
const READY_EVENT = 'percix:extension-ready';
// Dispatched on the document to ask a live bridge content script to re-stamp
// the id (bridge ≥ manifest 1.33 listens for it). Lets a tab left open across
// an extension update recover the fresh id without a page reload; harmless when
// no bridge is present (nothing answers, we fall back to the ready timeout).
const RESCAN_EVENT = 'percix:extension-rescan';
const READY_WAIT_MS = 800;

function readAdvertisedId(): string | undefined {
  const id = document.documentElement.dataset[ATTR];
  return id && id.length > 0 ? id : undefined;
}

export function getExtensionId(): string | undefined {
  const advertised = readAdvertisedId();

  if (PINNED_IDS.length === 0) {
    // Unpinned (local dev / unpacked): trust the DOM, as before.
    return advertised;
  }

  // Pinned: the DOM value is a PROPOSAL, honoured only if it is allowlisted.
  // This is what makes a hostile extension's advertised id inert.
  if (advertised && PINNED_IDS.indexOf(advertised) !== -1) {
    return advertised;
  }

  // Either nothing was advertised yet, or what was advertised is not ours.
  // Fall back to the primary pinned id rather than the DOM value: messaging an
  // id that is not installed fails cleanly (`extension_unreachable`), whereas
  // honouring an unrecognised id is the exact handover this pin prevents.
  return PRIMARY_PINNED_ID;
}

// True when this build talks only to allowlisted extensions. Exported so the
// handshake paths can report which trust mode they are in.
export function isExtensionIdPinned(): boolean {
  return PINNED_IDS.length > 0;
}

// The allowlist, for diagnostics and tests. Never used to pick a target.
export function pinnedExtensionIds(): readonly string[] {
  return PINNED_IDS;
}

// Ask a live bridge to re-announce, then resolve the (possibly refreshed) id.
// Unlike waitForExtensionId, this does NOT short-circuit on an already-present
// id — it always pokes the bridge first, so a STALE id (from a bridge context
// invalidated by an extension update) gets replaced. Resolves the current id
// once the bridge re-announces, or after READY_WAIT_MS (which may still be the
// stale/undefined value if no live bridge answered).
export function rescanExtensionId(): Promise<string | undefined> {
  // With exactly ONE pinned id there is nothing a rescan could discover — the
  // answer cannot change — so skip the bridge poke and its READY_WAIT_MS
  // timeout entirely.
  //
  // With SEVERAL (Chrome + Edge listings), the rescan is meaningful again: it
  // is how we learn WHICH of the allowlisted extensions is actually installed,
  // so fall through to the normal bridge round-trip.
  if (PINNED_IDS.length === 1) {
    return Promise.resolve(PRIMARY_PINNED_ID);
  }
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const refs: { onReady?: () => void; timer?: number } = {};
    const finish = (value: string | undefined) => {
      if (settled) {
        return;
      }
      settled = true;
      if (refs.onReady) {
        document.removeEventListener(READY_EVENT, refs.onReady);
      }
      if (refs.timer !== undefined) {
        window.clearTimeout(refs.timer);
      }
      resolve(value);
    };
    refs.onReady = () => finish(getExtensionId());
    document.addEventListener(READY_EVENT, refs.onReady);
    refs.timer = window.setTimeout(() => finish(getExtensionId()), READY_WAIT_MS);
    // Poke the bridge to re-stamp. If a live bridge hears it, it re-announces
    // (fires READY_EVENT) and we resolve immediately with the fresh id.
    document.dispatchEvent(new CustomEvent(RESCAN_EVENT));
  });
}

// Resolves to the extension id once it's available, or undefined after
// READY_WAIT_MS if no bridge content script ever ran.
export function waitForExtensionId(): Promise<string | undefined> {
  const existing = getExtensionId();
  if (existing) {
    return Promise.resolve(existing);
  }
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const refs: {
      onReady?: () => void;
      timer?: number;
    } = {};
    const finish = (value: string | undefined) => {
      if (settled) {
        return;
      }
      settled = true;
      if (refs.onReady) {
        document.removeEventListener(READY_EVENT, refs.onReady);
      }
      if (refs.timer !== undefined) {
        window.clearTimeout(refs.timer);
      }
      resolve(value);
    };
    refs.onReady = () => finish(getExtensionId());
    document.addEventListener(READY_EVENT, refs.onReady);
    refs.timer = window.setTimeout(() => finish(getExtensionId()), READY_WAIT_MS);
  });
}

export type ChromeRuntimeBridge = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    responseCallback: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string };
};

export function getChromeRuntime(): ChromeRuntimeBridge | undefined {
  const w = window as Window & { chrome?: { runtime?: ChromeRuntimeBridge } };
  return w.chrome?.runtime;
}
